import { isValueNotEmpty } from '@awp/helpers/utilsReact';
import format from 'date-fns/format';

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');

    tooltipEl.classList.add('custom-tooltip');

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const shouldShowItem = (itemData, itemType, showInvalidData = false) => {
  if (!itemData.isValid && itemData.displayY === null && !(itemData.isMissing && showInvalidData)) return false;
  if (itemData.isNan && !showInvalidData && itemType != 'TemperatureProfile') return false;

  return true;
};

const generateSoilLevelRow = (chart) => {
  let tr = null;
  const y = chart._active[0].element.$context.parsed.y;
  const soilLayers = chart.config.options.soilLayers;
  let tooltipSoilLevel = null;

  // Find the lowest soil level
  for (let x = soilLayers?.length - 1; x >= 0; x--) {
    if (y <= soilLayers[x].TopmostRelativeElevation) {
      tooltipSoilLevel = soilLayers[x];
      break;
    }
  }

  if (tooltipSoilLevel) {
    tr = document.createElement('tr');
    tr.style.backgroundColor = 'inherit';
    tr.style.borderWidth = 0;

    const td = document.createElement('td');
    td.style.borderWidth = 0;

    const text = document.createTextNode(`Soil Level: ${tooltipSoilLevel.soilType.Description}`);
    td.appendChild(text);
    tr.appendChild(td);
  }

  return tr;
};

export const externalTooltipHandler = (context) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  //frost probe has added a custom option element for tt handling of title
  const isFrostProbe = chart.config.options.frostProbe || false;
  const showInvalidData = chart.config.options.showInvalidData || false;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body && chart._active[0]) {
    //use the first active element to get the timestamp, should either be a line or temperature element
    const timestamp = chart._active[0].customData.data.x;

    //build the tooltip title
    tooltip.title = tooltip.dataPoints.map((l) => l.label);

    //const title = isFrostProbe ? ['Depth: ' + chart._active[0].element.$context.parsed.y + 'cm'] : tooltip.title[0] || [];

    let title;
    if (isFrostProbe) {
      const y = chart._active[0].element.$context.parsed.y;
      title = `Depth: ${y}cm`;
    } else {
      title = format(new Date(timestamp), 'MMM d, yyyy, H:mm:ss') || '';
    }

    const tableHead = document.createElement('thead');

    const tr = document.createElement('tr');
    tr.style.borderWidth = 0;

    const th = document.createElement('th');
    th.style.borderWidth = 0;
    const text = document.createTextNode(title);

    th.appendChild(text);
    tr.appendChild(th);
    tableHead.appendChild(tr);

    //build the tooltip body
    const tableBody = document.createElement('tbody');

    // Build Soil Level Description
    if (isFrostProbe) {
      const tr = generateSoilLevelRow(chart);
      if (tr) tableBody.appendChild(tr);
    }

    chart._active.forEach((item) => {
      const itemElement = item.customData;
      const itemData = itemElement.data;

      //work-around for WEAV-7384 & WEAV-10200
      if (!shouldShowItem(itemData, itemElement.type, showInvalidData)) return;

      const span = document.createElement('span');
      span.style.background = itemElement.legendColor;
      span.style.borderColor = itemElement.legendColor;
      span.style.borderWidth = '2px';
      span.style.marginRight = '10px';
      span.style.height = '10px';
      span.style.width = '10px';
      span.style.display = 'inline-block';

      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = 0;

      const td = document.createElement('td');
      td.style.borderWidth = 0;

      let displayedMeaning = '';

      //if yAxisList not null, always transform obs value to display order with displayedMeaning regardless transform
      if (isValueNotEmpty(itemElement.yAxisList)) {
        if (isValueNotEmpty(itemElement.yAxisList[itemData.y])) {
          displayedMeaning = ' ' + itemElement.yAxisList[itemData.y];
        }
      }

      let displayText = itemElement.datasetName + (itemElement.groupName ? ' (' + itemElement.groupName + '): ' : ': ');
      if (itemElement.type == 'TemperatureProfile') {
        if (itemData.isMissing) {
          displayText += 'Missing';
        } else if (itemData.isNan) {
          displayText += 'NaN ';
        } else {
          displayText += `${itemData.v}${itemElement.unitLabel}`;
        }
        displayText += ` @ ${itemData.y}cm` + '\xa0';
      } else {
        let displayValue = `${itemData.displayY}${itemElement.unitLabel}${displayedMeaning}`;

        displayValue = itemData.isMissing && showInvalidData ? 'Missing' : displayValue;
        displayValue = itemData.isNan && showInvalidData ? 'NaN ' : displayValue;
        displayText += `${displayValue}` + '\xa0';
      }

      const text = document.createTextNode(displayText);

      td.appendChild(span);
      td.appendChild(text);
      if (!itemData.isValid) {
        const invSpan = document.createElement('span');
        invSpan.classList.add('badge', 'badge-danger');
        invSpan.appendChild(document.createTextNode('!'));
        td.appendChild(invSpan);
      }

      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = positionX + tooltip.caretX + 'px';
  tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
  tooltipEl.style.minWidth = '125px';
  tooltipEl.style.maxWidth = '300px';
};
